import clsx from "clsx";
import { forwardRef } from "react";

import { Icon } from "./Icon";

interface FieldProps {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function Field({ className, children, disabled }: FieldProps) {
  return (
    <div className={clsx("space-y-2", className)} aria-disabled={disabled}>
      {children}
    </div>
  );
}

interface ColorInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange"
> {
  invalid?: boolean;
  onChange?: (value: string) => void;
}

export const ColorInput = forwardRef<HTMLInputElement, ColorInputProps>(
  ({ className, invalid, onChange, value, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value);
    };

    return (
      <div className="relative">
        <div
          className={clsx(
            "flex h-10 w-10 items-center justify-center rounded transition-all duration-200",
            value ? "ring-1 lg:ring-2 ring-gray-100" : "bg-gray-100",
          )}
          style={{ backgroundColor: (value as string) || "transparent" }}
        >
          <Icon
            name="fasFont"
            className={clsx(
              "h-4 w-4 transition-opacity duration-200",
              value ? "opacity-100" : "opacity-50",
              value ? "text-white" : "text-gray-400",
            )}
            aria-hidden="true"
          />
        </div>
        <input
          ref={ref}
          type="color"
          value={value}
          onChange={handleChange}
          className={clsx(
            "absolute inset-0 w-full h-full opacity-0 cursor-pointer",
            "before:content-[''] before:absolute before:inset-0 before:rounded-lg before:border-2 before:border-transparent",
            "hover:before:border-gray-200 focus:before:border-blue-200",
            "after:content-[''] after:absolute after:inset-0 after:rounded-lg after:bg-gradient-to-b after:from-white/20 after:to-transparent",
            invalid && "border-red-300 focus:border-red-500 focus:ring-red-500",
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);

ColorInput.displayName = "ColorInput";

interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "size"
> {
  indeterminate?: boolean;
  invalid?: boolean;
  labelClassName?: string;
  onChange?: (value: string) => void;
  rightLabel?: string;
  // Same naming as SelectInput's size; "xs" is a compact text box.
  size?: "normal" | "xs";
  toolTipText?: string;
  wrapText?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      invalid,
      type,
      labelClassName,
      onChange,
      value,
      rightLabel,
      toolTipText,
      indeterminate,
      wrapText,
      size = "normal",
      ...props
    },
    ref,
  ) => {
    const showPhoneIcon = type === "tel";
    const showEmailIcon = type === "email";
    const showToggle = type === "toggle";
    const showCheckbox = type === "checkbox";
    const showSearchIcon = type === "search";
    const showLeftIcon = showPhoneIcon || showEmailIcon || showSearchIcon;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (type === "tel") {
        // Remove all non-numeric characters except +
        let phoneNumber = e.target.value.replace(/[^\d+]/g, "");

        // Ensure + is only at the start
        if (phoneNumber.includes("+") && !phoneNumber.startsWith("+")) {
          phoneNumber = phoneNumber.replace(/\+/g, "");
        }

        // Add + if it's not there and we have numbers
        if (!phoneNumber.startsWith("+") && phoneNumber.length > 0) {
          phoneNumber = "+" + phoneNumber;
        }

        // Limit to reasonable length (15 is max E.164 length)
        phoneNumber = phoneNumber.slice(0, 16);

        onChange?.(phoneNumber);
      } else if (type === "toggle" || type === "checkbox") {
        onChange?.(e.target.checked ? "true" : "false");
      } else {
        onChange?.(e.target.value);
      }
    };

    const pattern = type === "tel" ? "\\+[1-9]\\d{1,14}" : undefined;
    const title =
      type === "tel"
        ? "Phone number in E.164 format: +[country code][number]"
        : undefined;

    if (showToggle || showCheckbox) {
      return (
        <label
          className={clsx(
            "relative inline-flex cursor-pointer items-center w-fit group",
            labelClassName,
          )}
        >
          <input
            ref={ref}
            type="checkbox"
            className="peer sr-only"
            checked={value === "true"}
            onChange={handleChange}
            {...props}
          />
          {showToggle ? (
            <div
              className={clsx(
                "relative h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200",
                "after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5",
                "after:rounded-full after:border after:border-gray-400 after:bg-white",
                "after:transition-transform after:duration-200 after:content-['']",
                "peer-checked:bg-gray-800 peer-checked:after:translate-x-full",
                "peer-checked:after:border-white",
                "peer-focus:ring-2 peer-focus:ring-gray-500/50",
                "hover:bg-gray-300 peer-checked:hover:bg-gray-700",
                invalid && "bg-red-100 peer-checked:bg-red-500",
              )}
            />
          ) : (
            <div
              className={clsx(
                "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors duration-200",
                {
                  "border-gray-300 bg-white":
                    !indeterminate && value !== "true",
                  "border-gray-800 bg-gray-800":
                    indeterminate || value === "true",
                },
                "peer-focus:ring-2 peer-focus:ring-gray-500/50",
                "hover:border-gray-400",
                invalid &&
                  "border-red-300 peer-checked:border-red-500 peer-checked:bg-red-500",
                props.disabled &&
                  "opacity-50 cursor-not-allowed bg-gray-50 border-gray-200",
              )}
            >
              {indeterminate ? (
                <svg
                  className="h-2.5 w-2.5 text-white transition-opacity duration-200"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20 12H4"
                  />
                </svg>
              ) : (
                <svg
                  className={clsx(
                    "h-2.5 w-2.5 text-white transition-opacity duration-200",
                    value === "true" ? "opacity-100" : "opacity-0",
                  )}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          )}
          <div className="relative inline-flex items-center flex-1 min-w-0">
            {rightLabel && (
              <div className="relative group">
                {wrapText ? (
                  <span
                    className={clsx(
                      "ml-2 lg:ml-3 select-none text-xs lg:text-sm text-gray-900 break-normal flex",
                      labelClassName,
                    )}
                  >
                    {rightLabel}
                  </span>
                ) : (
                  <span
                    className={clsx(
                      "ml-2 lg:ml-3 select-none text-xs lg:text-sm text-gray-900 break-normal overflow-hidden text-ellipsis whitespace-nowrap",
                      labelClassName,
                    )}
                  >
                    {rightLabel}
                  </span>
                )}
                {toolTipText && (
                  <div className="absolute bottom-full left-0 mb-2 w-max max-w-xs opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none z-10">
                    <div className="relative bg-gray-900 text-white text-xs rounded py-1 px-2">
                      {toolTipText}
                      <div className="absolute h-0 w-0 left-4 top-full border-x-4 border-x-transparent border-t-4 border-t-gray-900"></div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </label>
      );
    }

    return (
      <div className="relative">
        {showLeftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {showPhoneIcon && (
              <Icon
                name="fasMobile"
                className="h-4 w-4 text-gray-400"
                aria-hidden="true"
              />
            )}
            {showEmailIcon && (
              <Icon
                name="fasEnvelope"
                className="h-4 w-4 text-gray-400"
                aria-hidden="true"
              />
            )}
            {showSearchIcon && (
              <Icon
                name="magnifyingGlass"
                className="h-3 lg:h-4 w-3 lg:w-4 text-gray-400"
                aria-hidden="true"
              />
            )}
          </div>
        )}
        <input
          ref={ref}
          type={type === "tel" ? "text" : type}
          pattern={pattern}
          title={title}
          value={value}
          onChange={handleChange}
          onKeyPress={(e) => {
            if (type === "tel") {
              const isPlus = e.key === "+";
              const isNumber = /^[0-9]$/.test(e.key);
              if (!isPlus && !isNumber) {
                e.preventDefault();
              }
            }
          }}
          onPaste={(e) => {
            if (type === "tel") {
              e.preventDefault();
              const pastedText = e.clipboardData.getData("text");
              // Remove all non-digit characters except plus sign
              const cleanedNumber = pastedText.replace(/[^\d+]/g, "");
              // If the number doesn't start with +, add it
              const formattedNumber = cleanedNumber.startsWith("+")
                ? cleanedNumber
                : `+${cleanedNumber}`;
              // Update the input value
              const input = e.target as HTMLInputElement;
              input.value = formattedNumber;
              onChange?.(formattedNumber);
            }
          }}
          className={clsx(
            "block w-full border text-gray-900",
            size === "xs"
              ? "rounded-md py-1 text-xs placeholder:text-xs"
              : "rounded-lg py-2 text-xs lg:text-sm placeholder:text-xs placeholder:lg:text-sm",
            invalid
              ? "border-red-300 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:ring-gray-500",
            "focus:outline-none focus:ring-2",
            "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
            "transition-all duration-200 ease-in-out",
            showLeftIcon
              ? "pl-8 lg:pl-10 pr-2 lg:px-3"
              : size === "xs"
                ? "px-2"
                : "px-2 lg:px-3 ",
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
